import { CalendarDays, MapPin, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const surfaceTone = {
  highrisk: "border-rose-200 bg-rose-50 text-rose-700",
  reviewrequired: "border-amber-200 bg-amber-50 text-amber-700",
  safe: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const getLabelTone = (label) => surfaceTone[(label || "").toLowerCase()] || "border-slate-200 bg-slate-50 text-slate-700";

const FlaggedIssuesPanel = ({ compact = false }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedIssue, setSelectedIssue] = useState(null);

  useEffect(() => {
    const fetchFlaggedIssues = async () => {
      try {
        const res = await api.get("/admin/flagged-issues");
        setIssues(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching flagged issues:", err);
        toast.error("Failed to load flagged issues");
      } finally {
        setLoading(false);
      }
    };

    fetchFlaggedIssues();
  }, []);

  const moderationLabels = useMemo(
    () => [...new Set(issues.map((issue) => issue.moderationLabel).filter(Boolean))],
    [issues]
  );

  const visibleIssues = useMemo(() => {
    const filtered = issues.filter((issue) => {
      if (filter === "all") return true;
      return (issue.moderationLabel || "").toLowerCase() === filter;
    });
    return compact ? filtered.slice(0, 4) : filtered;
  }, [compact, filter, issues]);

  const handleAction = async (issueId, action) => {
    try {
      await api.patch(`/admin/flagged-issues/${issueId}/${action}`);
      setIssues((prev) =>
        prev
          .map((issue) => {
            if (issue._id !== issueId) return issue;
            if (action === "approve" || action === "ignore") return null;
            return { ...issue, status: "Flagged", flagged: true };
          })
          .filter(Boolean)
      );
      toast.success(`Issue ${action}d successfully`);
      setSelectedIssue(null);
    } catch (err) {
      console.error("Error updating flagged issue:", err);
      toast.error("Failed to update issue");
    }
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#ff9933]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!compact && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Flagged Issues</h2>
            <p className="text-sm text-slate-500">Moderation cases escalated by AI review.</p>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
          >
            <option value="all">All labels</option>
            {moderationLabels.map((label) => (
              <option key={label} value={label.toLowerCase()}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )}

      {visibleIssues.length === 0 ? (
        <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-10 text-center text-slate-500 shadow-sm">
          No flagged issues are waiting for review.
        </div>
      ) : (
        <div className="grid gap-4">
          {visibleIssues.map((issue, index) => (
            <motion.div
              key={issue._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
              className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-5 shadow-sm"
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${getLabelTone(issue.moderationLabel)}`}>
                      {issue.moderationLabel || "Unlabeled"}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {issue.createdAt ? new Date(issue.createdAt).toLocaleDateString() : "Date unavailable"}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{issue.title || "Untitled issue"}</h3>
                    <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-600">{issue.description || "No description provided."}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-[#fff1df] px-3 py-1 text-[#b76500]">{issue.category || "Other"}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-slate-600">
                      <MapPin className="h-3 w-3" />
                      {issue.constituency || "Unknown constituency"}
                    </span>
                    <span className="rounded-full bg-[#edf8ef] px-3 py-1 text-[#106d06]">
                      {issue.raisedBy?.name || issue.raisedBy?.phone || "Reporter unavailable"}
                    </span>
                  </div>
                </div>

                {!compact && (
                  <button
                    onClick={() => setSelectedIssue(issue)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                  >
                    Review Case
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {!compact && selectedIssue && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedIssue(null)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-[#fff1df] via-white to-[#edf8ef] px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
                  <ShieldAlert className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Flagged Issue Review</h3>
                  <p className="text-sm text-slate-500">Moderation action panel</p>
                </div>
              </div>
            </div>

            <div className="space-y-5 p-6">
              <div>
                <p className="text-sm font-medium text-slate-500">Title</p>
                <p className="text-lg font-semibold text-slate-900">{selectedIssue.title || "Untitled issue"}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Description</p>
                <p className="text-slate-700">{selectedIssue.description || "No description provided."}</p>
              </div>
              <div className="grid gap-4 text-sm text-slate-700 sm:grid-cols-2">
                <p>Category: {selectedIssue.category || "N/A"}</p>
                <p>Constituency: {selectedIssue.constituency || "N/A"}</p>
                <p>District: {selectedIssue.district || "N/A"}</p>
                <p>State: {selectedIssue.state || "N/A"}</p>
              </div>
              {selectedIssue.flaggedReason && (
                <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  {selectedIssue.flaggedReason}
                </div>
              )}
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={() => handleAction(selectedIssue._id, "approve")}
                  className="flex-1 rounded-2xl bg-[#138808] px-4 py-3 font-medium text-white transition-colors hover:bg-[#106d06]"
                >
                  Approve
                </button>
                <button
                  onClick={() => handleAction(selectedIssue._id, "reject")}
                  className="flex-1 rounded-2xl bg-[#d95d39] px-4 py-3 font-medium text-white transition-colors hover:bg-[#c44c29]"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleAction(selectedIssue._id, "ignore")}
                  className="flex-1 rounded-2xl bg-slate-900 px-4 py-3 font-medium text-white transition-colors hover:bg-slate-800"
                >
                  Ignore
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default FlaggedIssuesPanel;
